import { AppLink } from './Nav';
import { ExternalLink, Table, Td, Th, Tr, VideoBadge } from './data';
import { shareUrl } from '../lib/env';
import { bytes, timeAgo, timecode } from '../lib/format';
import type { VideoRow } from '../queries/admin';

/** The privacy scale in the few words a table row has room for. */
const VISIBILITY_LABELS: Record<VideoRow['visibility'], string> = {
  public: 'public',
  signedIn: 'signed-in only',
  team: 'team only',
  invited: 'named people',
  private: 'owner only',
};

/**
 * Recordings as a table, used on the recordings page and on user and team
 * detail pages. Callers that page supply their own body; the plain form
 * takes rows.
 */

export const VIDEO_COLUMNS = 7;

export function VideoHead({ showOwner = true }: { showOwner?: boolean }) {
  return (
    <thead>
      <tr>
        <Th>Recording</Th>
        {showOwner ? <Th>By</Th> : null}
        <Th>Where</Th>
        <Th>Status</Th>
        <Th align="right">Length</Th>
        <Th align="right">Views</Th>
        <Th align="right">When</Th>
      </tr>
    </thead>
  );
}

export function VideoTr({ video, showOwner = true }: { video: VideoRow; showOwner?: boolean }) {
  return (
    <Tr>
      <Td className="max-w-[22rem]">
        <span className="block truncate text-chalk" title={video.title}>
          {video.title}
        </span>
        <span className="flex items-center gap-2 text-caption text-chalk-faint">
          {video.kind === 'audio' ? 'audio' : VISIBILITY_LABELS[video.visibility]}
          {video.sizeBytes ? <span>{bytes(video.sizeBytes)}</span> : null}
          <ExternalLink href={shareUrl(video.shareId)}>open</ExternalLink>
        </span>
      </Td>
      {showOwner ? (
        <Td className="max-w-[14rem]">
          {video.owner ? (
            <AppLink to="/users/$id" params={{ id: video.owner.id }} className="block truncate text-chalk hover:underline">
              {video.owner.name?.trim() || video.owner.email}
            </AppLink>
          ) : video.submitterName ? (
            <span className="text-chalk-dim">{video.submitterName} (request link)</span>
          ) : (
            <span className="text-chalk-faint">nobody</span>
          )}
        </Td>
      ) : null}
      <Td className="max-w-[12rem]">
        {video.org ? (
          <AppLink to="/teams/$id" params={{ id: video.org.id }} className="block truncate text-chalk-dim hover:underline">
            {video.org.name}
          </AppLink>
        ) : (
          <span className="text-chalk-faint">personal</span>
        )}
      </Td>
      <Td>
        <VideoBadge status={video.status} deleted={video.deletedAt !== null} />
      </Td>
      <Td align="right">{timecode(video.durationSec)}</Td>
      <Td align="right">{video._count.views.toLocaleString()}</Td>
      <Td align="right" className="whitespace-nowrap text-chalk-dim" title={video.createdAt}>
        {timeAgo(video.createdAt)}
      </Td>
    </Tr>
  );
}

export function VideosTable({ videos, showOwner = true, empty = 'No recordings.' }: { videos: VideoRow[]; showOwner?: boolean; empty?: string }) {
  return (
    <Table>
      <VideoHead showOwner={showOwner} />
      <tbody>
        {videos.length === 0 ? (
          <tr>
            <td colSpan={VIDEO_COLUMNS} className="px-3 py-8 text-center text-body text-chalk-faint">
              {empty}
            </td>
          </tr>
        ) : (
          videos.map((video) => <VideoTr key={video.id} video={video} showOwner={showOwner} />)
        )}
      </tbody>
    </Table>
  );
}
